/**
 * Multi-select toggle group for the per-circuit lifecycle (Active /
 * Drafts / Released / Archived) — used by the Circuits tab inside a
 * project. Wears the same editorial vocabulary as the rest of the
 * frontend: mono ALL-CAPS labels, hairline rim on idle, status-tone
 * glow when selected, tabular-numeric counts inline.
 *
 * Defaults to {Active, Drafts}. Selecting nothing falls back to
 * "Active" so the page is never empty.
 *
 * NOTE: this is the *circuit*-level filter. The project list page uses
 * its own simpler `ProjectStatusFilter` (Active / Archived only) since
 * project-level archive is a separate concept from circuit lifecycle.
 */
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export type CircuitStatusFilterValue = Set<
  "active" | "draft" | "released" | "archived"
>;

type StatusKey = "active" | "draft" | "released" | "archived";

interface CircuitStatusFilterProps {
  value: CircuitStatusFilterValue;
  onChange: (next: CircuitStatusFilterValue) => void;
  /** Counts shown alongside each toggle. Pass undefined values for
   *  keys that haven't loaded yet; the toggle just hides the count. */
  counts?: Partial<Record<StatusKey, number>>;
}

const ORDER: Array<{ key: StatusKey; label: string }> = [
  { key: "active", label: "Active" },
  { key: "draft", label: "Drafts" },
  { key: "released", label: "Released" },
  { key: "archived", label: "Archived" },
];

export function CircuitStatusFilter({
  value,
  onChange,
  counts,
}: CircuitStatusFilterProps) {
  function toggle(key: StatusKey) {
    const next = new Set(value);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    // Never let the user empty the filter — that would render the page
    // blank. Snap back to "active" if they tried.
    if (next.size === 0) {
      next.add("active");
    }
    onChange(next);
  }

  return (
    <Box
      role="group"
      aria-label="Filter projects by status"
      sx={(t) => ({
        display: "inline-flex",
        alignItems: "stretch",
        borderRadius: 1.25,
        border: `1px solid ${t.palette.divider}`,
        bgcolor: "surface.raised",
        overflow: "hidden",
        // Hairline edge-glow on the group as a whole — frames the
        // toggle bar as a single instrument rather than four floating
        // chips. Subtle when nothing's selected, brighter when any
        // toggle is on.
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          borderRadius: "inherit",
          boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.06)}`,
        },
      })}
    >
      {ORDER.map(({ key, label }, idx) => {
        const selected = value.has(key);
        const count = counts?.[key];
        return (
          <StatusToggle
            key={key}
            statusKey={key}
            label={label}
            count={count}
            selected={selected}
            onClick={() => toggle(key)}
            isFirst={idx === 0}
            isLast={idx === ORDER.length - 1}
          />
        );
      })}
    </Box>
  );
}

interface StatusToggleProps {
  statusKey: StatusKey;
  label: string;
  count: number | undefined;
  selected: boolean;
  onClick: () => void;
  isFirst: boolean;
  isLast: boolean;
}

function StatusToggle({
  statusKey,
  label,
  count,
  selected,
  onClick,
  isFirst,
  isLast,
}: StatusToggleProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={(t) => {
        const tone =
          statusKey === "draft"
            ? t.palette.editorial.main
            : statusKey === "released"
              ? t.palette.success.main
              : statusKey === "archived"
                ? t.palette.text.disabled
                : t.palette.primary.main;
        // The outer group has `border: 1px solid` + `borderRadius:
        // 1.25` (5px outer). With a 1px border, the *inner* curve of
        // the container sits at 4px — round the outer-touching corners
        // of each toggle to 4px (= 1.0 in MUI units) so the toggle
        // nests cleanly inside the outer's inner curve. Otherwise the
        // selected-state inset rim leaves a gap at the corner where
        // the toggle's bigger arc gets clipped by overflow:hidden.
        const corner = 1.0;
        return {
          // reset button defaults
          font: "inherit",
          color: "inherit",
          background: "none",
          appearance: "none",
          cursor: "pointer",
          // layout
          display: "inline-flex",
          alignItems: "center",
          gap: 0.625,
          px: 1.25,
          py: 0.625,
          minHeight: 30,
          // dividers between toggles — hairline only, no full borders
          borderTop: "none",
          borderBottom: "none",
          borderLeft: isFirst ? "none" : `1px solid ${t.palette.divider}`,
          borderRight: "none",
          borderTopLeftRadius: isFirst ? corner : 0,
          borderBottomLeftRadius: isFirst ? corner : 0,
          borderTopRightRadius: isLast ? corner : 0,
          borderBottomRightRadius: isLast ? corner : 0,
          // accent fill + glow on selected
          bgcolor: selected ? alpha(tone, 0.14) : "transparent",
          transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          // small inset rim on selected — trace-style accent
          boxShadow: selected
            ? `inset 0 0 0 1px ${alpha(tone, 0.45)}`
            : "none",
          "&:hover": {
            bgcolor: selected ? alpha(tone, 0.2) : alpha(tone, 0.06),
          },
          "&:focus-visible": {
            outline: `2px solid ${alpha(tone, 0.5)}`,
            outlineOffset: -2,
          },
        };
      }}
    >
      <Typography
        component="span"
        sx={(t) => {
          const tone =
            statusKey === "draft"
              ? t.palette.editorial.main
              : statusKey === "released"
                ? t.palette.success.main
                : statusKey === "archived"
                  ? t.palette.text.disabled
                  : t.palette.primary.light;
          return {
            fontFamily: t.fonts.mono,
            fontSize: "0.6875rem",
            fontWeight: selected ? 600 : 500,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: selected ? tone : "text.secondary",
            lineHeight: 1,
          };
        }}
      >
        {label}
      </Typography>
      {count != null && count > 0 && (
        <Typography
          component="span"
          sx={(t) => {
            const tone =
              statusKey === "draft"
                ? t.palette.editorial.main
                : statusKey === "released"
                  ? t.palette.success.main
                  : statusKey === "archived"
                    ? t.palette.text.disabled
                    : t.palette.primary.light;
            return {
              fontFamily: t.fonts.mono,
              fontSize: "0.6875rem",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: selected ? tone : "text.disabled",
              lineHeight: 1,
              opacity: 0.85,
            };
          }}
        >
          {count}
        </Typography>
      )}
    </Box>
  );
}
